/**
 * Image Upload and Compression Utility
 * Handles image compression to 30KB and uploads to Supabase Storage
 */

import { supabase } from '../services/supabaseClient';

// Maximum file size in bytes (30KB)
const MAX_FILE_SIZE = 30 * 1024; // 30KB
const TARGET_FILE_SIZE = 28 * 1024; // 28KB (target slightly lower for safety)

/**
 * Compress image to target size
 * @param {File} file - Original image file
 * @param {number} targetSize - Target size in bytes
 * @returns {Promise<Blob>} - Compressed image blob
 */
export const compressImage = async (file, targetSize = TARGET_FILE_SIZE) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Calculate dimensions to maintain aspect ratio
        let width = img.width;
        let height = img.height;
        
        // Start with smaller dimensions for better compression
        const maxDimension = 600; // Reduced from 800
        if (width > height && width > maxDimension) {
          height = (height / width) * maxDimension;
          width = maxDimension;
        } else if (height > maxDimension) {
          width = (width / height) * maxDimension;
          height = maxDimension;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw image on canvas
        ctx.drawImage(img, 0, 0, width, height);
        
        // Improved compression with quality steps
        const attemptCompression = async (quality, scaleFactor = 1.0) => {
          return new Promise((resolveAttempt) => {
            // Adjust canvas size if scale factor changed
            if (scaleFactor < 1.0) {
              const newWidth = Math.floor(width * scaleFactor);
              const newHeight = Math.floor(height * scaleFactor);
              canvas.width = newWidth;
              canvas.height = newHeight;
              ctx.drawImage(img, 0, 0, newWidth, newHeight);
            }
            
            canvas.toBlob(
              (blob) => {
                resolveAttempt(blob);
              },
              'image/jpeg',
              quality
            );
          });
        };
        
        // Try different quality and size combinations
        const tryCompressSequence = async () => {
          const attempts = [
            { quality: 0.7, scale: 1.0 },   // Try quality 0.7 first
            { quality: 0.5, scale: 1.0 },   // Lower quality
            { quality: 0.4, scale: 0.9 },   // Lower quality + smaller size
            { quality: 0.3, scale: 0.8 },   // Even lower
            { quality: 0.2, scale: 0.7 },   // Last resort
            { quality: 0.1, scale: 0.6 }    // Absolute minimum
          ];
          
          for (const attempt of attempts) {
            const blob = await attemptCompression(attempt.quality, attempt.scale);
            if (blob && blob.size <= targetSize) {
              resolve(blob);
              return;
            }
          }
          
          // If nothing worked, return the smallest we got
          const finalBlob = await attemptCompression(0.1, 0.5);
          console.warn(`Image compressed to ${(finalBlob.size / 1024).toFixed(2)}KB (target: ${(targetSize / 1024).toFixed(2)}KB)`);
          resolve(finalBlob);
        };
        
        tryCompressSequence();
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Validate image file
 * @param {File} file - Image file to validate
 * @returns {Object} - { valid: boolean, error: string }
 */
export const validateImage = (file) => {
  // Check if file exists
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }
  
  // Check file type
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!validTypes.includes(file.type)) {
    return { 
      valid: false, 
      error: 'Invalid file type. Please upload JPEG, PNG, or WebP images only.' 
    };
  }
  
  // Check file size (before compression)
  const maxInitialSize = 5 * 1024 * 1024; // 5MB initial size limit
  if (file.size > maxInitialSize) {
    return { 
      valid: false, 
      error: `Image is too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Please choose an image under 5MB.` 
    };
  }
  
  return { valid: true, error: null };
};

/**
 * Upload image via backend (bypasses Supabase RLS)
 * @param {Blob} imageBlob - Compressed image blob
 * @param {string} folder - Storage folder (e.g., 'products', 'categories')
 * @returns {Promise<Object>} - { url: string, path: string, error: null } or { url: null, path: null, error: string }
 */
export const uploadImageViaBackend = async (imageBlob, folder = 'products') => {
  try {
    // Import adminAuthService dynamically to avoid circular dependency
    const adminAuthService = (await import('../services/adminAuthService')).default;
    
    // Create FormData
    const formData = new FormData();
    formData.append('image', imageBlob, 'image.jpg');
    formData.append('folder', folder);

    console.log('📤 Uploading via backend:', {
      size: imageBlob.size,
      sizeKB: (imageBlob.size / 1024).toFixed(2),
      folder
    });

    // Get auth session
    const session = adminAuthService.getCurrentSession();
    if (!session?.access_token) {
      console.error('❌ No auth session found');
      return { url: null, path: null, error: 'Not authenticated. Please login again.' };
    }

    // Upload to backend
    const response = await fetch('http://localhost:4002/api/admin/upload-image', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`
        // Don't set Content-Type - browser will set it with boundary for multipart/form-data
      },
      body: formData
    });

    const result = await response.json();

    if (!result.success) {
      console.error('❌ Backend upload failed:', result.error);
      return { url: null, path: null, error: result.error };
    }

    console.log('✅ Backend upload successful:', result);
    
    return {
      url: result.url,
      path: result.path,
      error: null
    };
  } catch (err) {
    console.error('❌ Backend upload exception:', err);
    return { url: null, path: null, error: err.message };
  }
};

/**
 * Upload image to Supabase Storage (DEPRECATED - use uploadImageViaBackend)
 * Kept for reference, but RLS issues make this unreliable
 * @param {Blob} imageBlob - Compressed image blob
 * @param {string} folder - Storage folder (e.g., 'products', 'categories')
 * @param {string} filename - Custom filename (optional)
 * @returns {Promise<Object>} - { url: string, path: string, error: null } or { url: null, path: null, error: string }
 */
export const uploadImageToSupabase = async (imageBlob, folder = 'products', filename = null) => {
  try {
    // Debug: Check Supabase client state
    console.log('🔍 Supabase client check:', {
      hasClient: !!supabase,
      hasStorage: !!supabase?.storage,
      url: supabase?.supabaseUrl || 'unknown',
      keyPrefix: supabase?.supabaseKey?.substring(0, 20) + '...' || 'unknown'
    });
    
    // Generate unique filename
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(7);
    const extension = 'jpg'; // Always save as JPEG after compression
    const finalFilename = filename || `${folder}_${timestamp}_${randomString}.${extension}`;
    const filePath = `${folder}/${finalFilename}`;
    
    console.log('📤 Attempting upload:', {
      bucket: 'product-images',
      path: filePath,
      size: imageBlob.size,
      type: imageBlob.type
    });
    
    // Check Supabase auth state
    const { data: { session } } = await supabase.auth.getSession();
    console.log('🔐 Auth session:', session ? 'Authenticated' : 'Anonymous');
    
    // Upload to Supabase Storage
    // Use public upload with anonymous access
    const { data, error } = await supabase.storage
      .from('product-images') // Bucket name
      .upload(filePath, imageBlob, {
        contentType: 'image/jpeg',
        cacheControl: '3600',
        upsert: false
      });
    
    if (error) {
      console.error('Supabase upload error:', error);
      console.error('Error details:', {
        message: error.message,
        statusCode: error.statusCode,
        error: error.error,
        name: error.name
      });
      console.log('Upload attempt details:', {
        bucket: 'product-images',
        path: filePath,
        size: imageBlob.size,
        type: imageBlob.type
      });
      return { url: null, path: null, error: error.message };
    }
    
    console.log('✅ Upload successful! File path:', filePath);
    console.log('Upload response data:', data);
    
    // Get public URL
    const { data: urlData } = supabase.storage
      .from('product-images')
      .getPublicUrl(filePath);
    
    return {
      url: urlData.publicUrl,
      path: filePath,
      error: null
    };
  } catch (err) {
    console.error('Upload error:', err);
    return { url: null, path: null, error: err.message };
  }
};

/**
 * Delete image from Supabase Storage
 * @param {string} filePath - Path to file in storage
 * @returns {Promise<Object>} - { success: boolean, error: string }
 */
export const deleteImageFromSupabase = async (filePath) => {
  try {
    if (!filePath) {
      return { success: false, error: 'No file path provided' };
    }
    
    const { error } = await supabase.storage
      .from('product-images')
      .remove([filePath]);
    
    if (error) {
      console.error('Delete error:', error);
      return { success: false, error: error.message };
    }
    
    return { success: true, error: null };
  } catch (err) {
    console.error('Delete error:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Complete image upload flow with compression and validation
 * @param {File} file - Original image file
 * @param {string} folder - Storage folder
 * @param {Function} onProgress - Progress callback (optional)
 * @returns {Promise<Object>} - Upload result
 */
export const uploadImage = async (file, folder = 'products', onProgress = null) => {
  try {
    // Validate image
    const validation = validateImage(file);
    if (!validation.valid) {
      return { success: false, url: null, path: null, error: validation.error };
    }
    
    // Update progress
    if (onProgress) onProgress({ stage: 'validating', progress: 10 });
    
    // Compress image
    if (onProgress) onProgress({ stage: 'compressing', progress: 30 });
    const compressedBlob = await compressImage(file, TARGET_FILE_SIZE);
    
    // Check compressed size
    if (compressedBlob.size > MAX_FILE_SIZE) {
      return { 
        success: false, 
        url: null, 
        path: null, 
        error: `Compressed image is still too large (${(compressedBlob.size / 1024).toFixed(2)}KB). Please use a simpler image.` 
      };
    }
    
    // Update progress
    if (onProgress) onProgress({ 
      stage: 'compressed', 
      progress: 60, 
      size: compressedBlob.size,
      sizeKB: (compressedBlob.size / 1024).toFixed(2)
    });
    
    // Upload via backend (uses service role, bypasses RLS)
    if (onProgress) onProgress({ stage: 'uploading', progress: 70 });
    const uploadResult = await uploadImageViaBackend(compressedBlob, folder);
    
    if (uploadResult.error) {
      return { success: false, url: null, path: null, error: uploadResult.error };
    }
    
    // Complete
    if (onProgress) onProgress({ stage: 'complete', progress: 100 });
    
    return {
      success: true,
      url: uploadResult.url,
      path: uploadResult.path,
      size: compressedBlob.size,
      sizeKB: (compressedBlob.size / 1024).toFixed(2),
      error: null
    };
  } catch (err) {
    console.error('Upload flow error:', err);
    return { success: false, url: null, path: null, error: err.message };
  }
};

/**
 * Preview image file
 * @param {File} file - Image file
 * @returns {Promise<string>} - Data URL for preview
 */
export const previewImage = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsDataURL(file);
  });
};

/**
 * Get file size in human-readable format
 * @param {number} bytes - Size in bytes
 * @returns {string} - Formatted size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

const imageUploadUtils = {
  uploadImage,
  compressImage,
  validateImage,
  uploadImageToSupabase,
  uploadImageViaBackend,
  deleteImageFromSupabase,
  previewImage,
  formatFileSize,
  MAX_FILE_SIZE,
  TARGET_FILE_SIZE
};

export default imageUploadUtils;
